import type { StateCreator } from 'zustand'
import type { loadingState } from './loadingSlice'
import type { authSessionState } from './authSessionSlice'
import { toast } from 'sonner'
import { OrganizationUser, InviteUserRequest, type UserRole } from '@/types/users'
import { apiService } from '@/service/api'

export interface UsersState {
  organizationName: string | null
  organizationUsers: OrganizationUser[]
  isUsersLoading: boolean
  /**
   * `passive` marks a background read that the user did not ask for — a
   * comment surface resolving author names, say. It fetches the list and
   * NOTHING else: no failure toast (no error about a list nobody requested)
   * and no writes to state the caller has no business changing. Settings ->
   * Team calls it plain, where a toast and the org header ARE the response.
   */
  fetchOrganizationUsers: (options?: { passive?: boolean }) => Promise<void>
  inviteUserToOrganization: (request: InviteUserRequest) => Promise<string>
  updateUserRole: (userId: string, role: UserRole) => Promise<void>
  deleteOrganizationUser: (userId: string) => Promise<boolean>
  revokeOrganizationInvite: (inviteId: string) => Promise<void>
  setUsersLoading: (isUsersLoading: boolean) => void
}

const messages = {
  success: {
    inviteUser: 'User invited successfully',
    updateUserRole: 'User role updated successfully',
    deleteUser: 'User deleted successfully',
    revokeInvite: 'Invitation revoked successfully',
  },
  error: {
    inviteUser: 'Error inviting user',
    fetchOrganizationUsers: 'Error fetching organization users',
    updateUserRole: 'Error updating user role',
    deleteUser: 'Error deleting user',
    revokeInvite: 'Error revoking invitation',
  },
}

export const usersSlice: StateCreator<
  UsersState & loadingState & authSessionState,
  [],
  [],
  UsersState
> = (set, get) => ({
  organizationName: null,
  organizationUsers: [],
  isUsersLoading: true,

  setUsersLoading: (isUsersLoading) => {
    set({ isUsersLoading })
  },

  /**
   * Fetches organization users from Keycloak via backend API.
   * All user data (name, picture, role, status) comes from Keycloak.
   * The backend handles Keycloak Admin API calls.
   */
  fetchOrganizationUsers: async (options?: { passive?: boolean }) => {
    const { setUsersLoading } = get()

    try {
      setUsersLoading(true)
      const data = await apiService.getOrganizationUsers()
      
      const users = data.users.map(user => {
        const status = user.status as 'active' | 'pending' | 'inactive'
        const joinedAt = status === 'pending' ? user.invited_at : user.created_at
        const rowId = user.user_id ?? user.invite_id ?? null
        
        return {
          user_id: user.user_id,
          invite_id: user.invite_id ?? null,
          email: user.email,
          name: user.name,
          picture: user.picture,
          role: user.role as UserRole,
          status,
          joined_at: joinedAt,
          id: rowId || undefined,
          added_on: joinedAt,
        }
      })
      
      // A passive read must not touch `organizationName`: ShareModal and
      // ShareRecipientPicker render it ("Anyone at {organization}"), so a
      // board's background directory fetch would otherwise flip their copy
      // from "your organization" to the placeholder this line writes.
      set(
        options?.passive
          ? { organizationUsers: users }
          : { organizationName: 'Organization', organizationUsers: users }
      )
    } catch {
      if (!options?.passive) {
        toast.error(messages.error.fetchOrganizationUsers)
      }
      set({ organizationUsers: [] })
    } finally {
      setUsersLoading(false)
    }
  },

  /**
   * Invites a user to the organization via Keycloak.
   * Sends invitation email and assigns organization membership + role.
   * The backend handles Keycloak Admin API calls.
   */
  inviteUserToOrganization: async (request: InviteUserRequest): Promise<string> => {
    const { setUsersLoading, fetchOrganizationUsers } = get()

    try {
      setUsersLoading(true)
      const response = await apiService.inviteUserToOrganization({
        email: request.email,
        role: request.role
      })
      await fetchOrganizationUsers()
      toast.success(messages.success.inviteUser)
      return response.user_id
    } catch (error) {
      toast.error(messages.error.inviteUser)
      throw error
    } finally {
      setUsersLoading(false)
    }
  },

  /**
   * Updates a user's role in the organization via Keycloak.
   * The backend handles Keycloak Admin API calls.
   */
  updateUserRole: async (userId: string, role: UserRole) => {
    const { setUsersLoading, fetchOrganizationUsers } = get()

    try {
      setUsersLoading(true)
      await apiService.updateUserRole(userId, { role })
      await fetchOrganizationUsers()
      toast.success(messages.success.updateUserRole)
    } catch {
      toast.error(messages.error.updateUserRole)
    } finally {
      setUsersLoading(false)
    }
  },

  /**
   * Removes a user from the organization via Keycloak.
   * The backend handles Keycloak Admin API calls.
   */
  deleteOrganizationUser: async (userId: string) => {
    const { setUsersLoading, fetchOrganizationUsers } = get()

    try {
      setUsersLoading(true)
      await apiService.deleteOrganizationUser(userId)
      await fetchOrganizationUsers()
      toast.success(messages.success.deleteUser)
      return true
    } catch {
      toast.error(messages.error.deleteUser)
      return false
    } finally {
      setUsersLoading(false)
    }
  },

  /**
   * Revokes a pending invitation via Keycloak.
   * The backend handles Keycloak Admin API calls.
   */
  revokeOrganizationInvite: async (inviteId: string) => {
    const { setUsersLoading, fetchOrganizationUsers } = get()

    try {
      setUsersLoading(true)
      await apiService.revokeOrganizationInvite(inviteId)
      await fetchOrganizationUsers()
      toast.success(messages.success.revokeInvite)
    } catch (error) {
      toast.error(messages.error.revokeInvite)
      throw error
    } finally {
      setUsersLoading(false)
    }
  },
})
